// TerritoryQuickCreateModal.js
import React, { useState, useEffect } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Autocomplete,
  InputAdornment,
  Divider,
} from "@mui/material";

import {
  MapPin,
  FilePlus2,
  UserRound,
  UsersRound,
  Search,
  Info,
} from "lucide-react";

import CrmDialog from "../components/CrmDialog";
import ModalHeader from "../components/ModalHeader";

import API from "../utils/api";
import { toast } from "react-toastify";

export default function TerritoryQuickCreateModal({
  open,
  onClose,
  onCreated,
  isReadOnly = false,
}) {
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    name: "",
    manager: "",
    reps: [],
    lead_count: "",
  });

  const resetForm = () =>
    setForm({
      name: "",
      manager: "",
      reps: [],
      lead_count: "",
    });

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await API.get("/users/");
        setUsers(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchUsers();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreate = async () => {
    if (!form.name || !form.manager) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      await API.post("/territories/", {
        name: form.name,
        manager: form.manager,
        assigned_reps: form.reps,
        lead_count: form.lead_count || 0,
      });

      toast.success("Territory created!");
      onCreated();
      onClose();

      setForm({
        name: "",
        manager: "",
        reps: [],
        lead_count: "",
      });
    } catch (err) {
      toast.error("Failed to create territory.");
      console.error(err);
    }
  };

  // ------------------------------------------------------------
  // Shared V2 styles
  // ------------------------------------------------------------

  const sectionSx = {
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(15,23,42,0.08)",

    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.025)"
        : "rgba(248,250,252,0.55)",

    borderRadius: 3,
    p: { xs: 2, sm: 2.25 },
  };

  const iconBubbleSx = {
    width: 42,
    height: 42,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,

    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(33,150,243,0.12)"
        : "rgba(33,150,243,0.08)",

    color: "primary.main",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.25,
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.025)"
          : "background.paper",

      "& fieldset": {
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(15,23,42,0.12)",
      },

      "&:hover fieldset": {
        borderColor: "primary.main",
      },

      "&.Mui-focused fieldset": {
        borderWidth: 1.5,
      },
    },

    "& .MuiInputBase-input": {
      fontSize: "0.875rem",
    },

    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: 2.5,

        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(15,23,42,0.06)",

        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 10px 28px rgba(0,0,0,0.30)"
            : "0 10px 28px rgba(15,23,42,0.09)",

        p: 0.5,

        "& .MuiMenuItem-root": {
          fontSize: "0.8125rem",
          minHeight: 36,
          borderRadius: 1.5,
          px: 1.25,
        },
      },
    },
  };

  return (
    <CrmDialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      {/* =========================================================
          HEADER
      ========================================================= */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2.25, sm: 2.5 },
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            ...iconBubbleSx,
            width: 50,
            height: 50,
          }}
        >
          <MapPin size={24} strokeWidth={2} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: "1.15rem", sm: "1.3rem" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "text.primary",
              lineHeight: 1.25,
            }}
          >
            Add Territory
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              fontSize: "0.84rem",
              color: "text.secondary",
              lineHeight: 1.45,
            }}
          >
            Create a new territory and assign a manager.
          </Typography>
        </Box>
      </Box>

      {/* =========================================================
          CONTENT
      ========================================================= */}

      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          {/* =====================================================
              TERRITORY DETAILS
          ===================================================== */}

          <Box sx={sectionSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 2,
              }}
            >
              <Box sx={iconBubbleSx}>
                <FilePlus2 size={20} strokeWidth={2} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 650,
                    color: "text.primary",
                  }}
                >
                  Territory Details
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    color: "text.secondary",
                    mt: 0.15,
                  }}
                >
                  Name the territory and choose its manager.
                </Typography>
              </Box>
            </Box>

            {/* Territory Name */}

            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 0.75,
                }}
              >
                Territory Name{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter territory name (e.g. Atlanta, Dallas, etc.)"
                value={form.name}
                onChange={(e) =>
                  handleChange("name", e.target.value)
                }
                disabled={isReadOnly}
                sx={fieldSx}
              />

              <Typography
                sx={{
                  mt: 0.65,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                }}
              >
                The name will appear throughout the system.
              </Typography>
            </Box>

            {/* Manager */}

            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 0.75,
                }}
              >
                Manager{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography>

              <TextField
                select
                fullWidth
                size="small"
                value={form.manager}
                onChange={(e) =>
                  handleChange("manager", e.target.value)
                }
                disabled={isReadOnly}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: menuProps,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <UserRound
                        size={18}
                        strokeWidth={1.8}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              >
                <MenuItem value="" disabled>
                  Select a manager
                </MenuItem>

                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </MenuItem>
                ))}
              </TextField>

              <Typography
                sx={{
                  mt: 0.65,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                }}
              >
                The assigned manager will have access to this territory.
              </Typography>
            </Box>
          </Box>

          {/* =====================================================
              INITIAL LEAD ASSIGNMENT
          ===================================================== */}

          <Box sx={sectionSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 1.75,
              }}
            >
              <Box sx={iconBubbleSx}>
                <UsersRound size={20} strokeWidth={2} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 650,
                    color: "text.primary",
                  }}
                >
                  Initial Rep Assignment{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 400,
                    }}
                  >
                    (Optional)
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    color: "text.secondary",
                    mt: 0.15,
                  }}
                >
                  Select reps to assign to this territory. You can modify
                  this later.
                </Typography>
              </Box>
            </Box>

            <Autocomplete
              multiple
              fullWidth
              options={users}
              getOptionLabel={(u) =>
                `${u.first_name} ${u.last_name}`
              }
              value={users.filter((u) =>
                form.reps.includes(u.id)
              )}
              onChange={(e, newVal) =>
                handleChange(
                  "reps",
                  newVal.map((r) => r.id)
                )
              }
              disabled={isReadOnly}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(15,23,42,0.06)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 10px 28px rgba(0,0,0,0.30)"
                        : "0 10px 28px rgba(15,23,42,0.09)",

                    "& .MuiAutocomplete-option": {
                      fontSize: "0.8125rem",
                      minHeight: 36,
                      borderRadius: 1.5,
                      mx: 0.5,
                      my: 0.25,
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    form.reps.length === 0
                      ? "Search and select reps..."
                      : ""
                  }
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search
                            size={17}
                            strokeWidth={1.8}
                          />
                        </InputAdornment>

                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* =====================================================
              INFO PANEL
          ===================================================== */}

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              px: 2,
              py: 1.6,

              borderRadius: 2.5,
              border: "1px solid",

              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(33,150,243,0.18)"
                  : "rgba(33,150,243,0.16)",

              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(33,150,243,0.07)"
                  : "rgba(33,150,243,0.055)",
            }}
          >
            <Box
              sx={{
                ...iconBubbleSx,
                width: 36,
                height: 36,
              }}
            >
              <Info size={18} strokeWidth={2} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 650,
                  color: "text.primary",
                  mb: 0.25,
                }}
              >
                What happens next?
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  color: "text.secondary",
                }}
              >
                After creating the territory, you can upload leads or add
                individual leads from the territory page.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* =========================================================
          ACTIONS
      ========================================================= */}

      <Box sx={{ px: { xs: 2, sm: 2.5 } }}>
        <Divider />
      </Box>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: 1.75,
          pb: { xs: 2, sm: 2.5 },
          gap: 1,

          flexDirection: {
            xs: "column-reverse",
            sm: "row",
          },

          "& > :not(style) ~ :not(style)": {
            ml: {
              xs: "0 !important",
              sm: "8px !important",
            },
          },
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          fullWidth
          sx={{
            maxWidth: { sm: 130 },
            minHeight: 42,
            borderRadius: 2.25,
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 600,
            color: "text.primary",
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.18)"
                : "rgba(15,23,42,0.18)",

            "&:hover": {
              borderColor: "text.secondary",
              bgcolor: "action.hover",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isReadOnly}
          fullWidth
          sx={{
            maxWidth: { sm: 160 },
            minHeight: 42,
            borderRadius: 2.25,
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 650,
            boxShadow: (theme) =>
              `0 7px 18px ${theme.palette.primary.main}35`,

            "&:hover": {
              boxShadow: (theme) =>
                `0 9px 22px ${theme.palette.primary.main}45`,
            },
          }}
        >
          Create Territory
        </Button>
      </DialogActions>
    </CrmDialog>
  );
}